import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { HaloLoaderService } from 'src/app/core/halo-loader/halo-loader.service';
import {
  PerformanceRun,
  PerformanceRunAssets,
  PerformanceRunKeysType,
  VisibleRowLayout,
} from './performance-run.interface';
import { PerformanceRunService } from './performance-run.service';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { CommonUtil } from 'src/app/core/Utils/common-util';
import { environment } from 'src/environments/environment';
import { HaloToastService } from 'src/app/core/halo-toast/halo-toast.service';
import { SelectionInfo } from '../../selectors/multi-selection/multi-selection.model';

@Component({
  selector: 'app-performance-run',
  templateUrl: './performance-run.component.html',
  styleUrls: ['./performance-run.component.scss'],
})
export class PerformanceRunComponent implements OnInit, OnDestroy {
  hasFullPermission: boolean;
  performanceRunData: PerformanceRun;
  performanceRunForm: FormGroup;
  showConfirmationModal = false;
  performanceRunAssetsList: PerformanceRunAssets[];
  haloWebCloudBasePath: string;
  selectedLayoutImage: string;
  checkIcon: string;
  connectedCount: string;
  notConnectedCount: string;
  assetIds: string[] = [];
  isSaved = false;
  private selectionInfo: SelectionInfo;
  private _destroy$: Subject<void> = new Subject<void>();
  private _consoleImageMap: Map<string, any> = new Map<string, any>([
    [
      'aquaman',
      {
        sixLayout: '/git/Images/Image_PerformanceRun_6Layout_AQUAMAN.png',
        tenLayout: '/git/Images/Image_PerformanceRun_10Layout_AQUAMAN.png',
      },
    ],
    ['mulder', { sixLayout: '/git/Images/Image_PerformanceRun_6Layout_MULDER.png', tenLayout: '' }],
    [
      'superman',
      {
        sixLayout: '/git/Images/Image_PerformanceRun_6Layout_SUPERMAN.png',
        tenLayout: '/git/Images/Image_PerformanceRun_10Layout_SUPERMAN.png',
      },
    ],
    [
      'hal',
      {
        sixLayout: '/git/Images/Image_PerformanceRun_6Layout_HAL.png',
        tenLayout: '/git/Images/Image_PerformanceRun_10Layout_HAL.png',
      },
    ],
    [
      'bluth',
      {
        sixLayout: '/git/Images/Image_PerformanceRun_6Layout_BLUTH.png',
        tenLayout: '/git/Images/Image_PerformanceRun_10Layout_BLUTH.png',
      },
    ],
  ]);

  constructor(
    private _performanceRunService: PerformanceRunService,
    private _loader: HaloLoaderService,
    private _commonUtil: CommonUtil,
    private _toast: HaloToastService
  ) {}

  ngOnInit(): void {
    this.haloWebCloudBasePath = environment.haloWebCloudBasePath;
    this.checkIcon = `${this.haloWebCloudBasePath}/git/Icons/LFconnect_Icon_Checkmark_Green_12x12.png`;
  }

  isValidatePerformanceData(): boolean {
    return !!(
      this.performanceRunData?.visibleSixRowLayoutValues ||
      this.performanceRunData?.visibleSixRowLayoutValues_high ||
      this.performanceRunData?.visibleTenRowLayoutValues ||
      this.performanceRunData?.visibleTenRowLayoutValues_high
    );
  }

  chooseEquipment(): void {
    this._loader.startProcess();
    this._performanceRunService
      .getPerformanceRunEquipList(this._getRequestPayload())
      .pipe(takeUntil(this._destroy$))
      .subscribe((data) => {
        if (data) {
          this.connectedCount = data.connectedCount;
          this.notConnectedCount = data.notConnectedCount;
          this.performanceRunAssetsList = data.performanceRunAssetsList;
          this.performanceRunAssetsList.forEach((item) => {
            if (item.published === 'true') {
              this.assetIds.push(item.assetId);
            }
          });
          this._loader.endProcess();
          this.showConfirmationModal = true;
        }
      });
  }

  clickedOnItem(item: any, status: boolean): void {
    if (this.hasFullPermission) {
      item.isClickedOnPublish = status;
      if (status) {
        this.assetIds = [...this.assetIds, item.assetId];
      } else {
        this.assetIds = this.assetIds.filter((id) => id !== item.assetId);
      }
    }
  }

  isAllEquipChecked(): boolean {
    return !!!this.performanceRunAssetsList.find((item) => item.published === 'false');
  }

  selectAllEquip(): void {
    if (this.hasFullPermission) {
      this.assetIds = [];
      this.performanceRunAssetsList = this.performanceRunAssetsList.map((item) => {
        this.assetIds.push(item.assetId);
        return {
          ...item,
          isClickedOnPublish: true,
        };
      });
    }
  }

  resetAllEquip(): void {
    if (this.hasFullPermission) {
      this.assetIds = [];
      this.performanceRunAssetsList = this.performanceRunAssetsList.map((item) => {
        if (item.published === 'true') {
          this.assetIds.push(item.assetId);
        }
        return {
          ...item,
          isClickedOnPublish: false,
        };
      });
    }
  }

  isClickedOnSelectAllEquip(): boolean {
    return !!!this.performanceRunAssetsList.find((item) => item.isClickedOnPublish === false);
  }

  closeModal(): void {
    this.assetIds = [];
    this.showConfirmationModal = false;
    this.isSaved = false;
  }

  confirmationModalConfirm(): void {
    const body = this._getSavePerformanceRunRequestPayload();
    this._loader.startProcess();
    this._performanceRunService
      .savePerformanceRun(body)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.selectionInfo.rootScopeData.isPageHasUnsavedChanges = false;
        this.isSaved = true;
        this._loader.endProcess();
        this.closeModal();
        this._toast.creatToast(this._commonUtil.translateText('PERFORMANCE.RUN.PUBLISHED'));
      });
  }

  changeLeftInputField(formControl: FormControl): void {
    this.selectionInfo.rootScopeData.isPageHasUnsavedChanges = true;
    if (formControl.value < this.getInclineMinVal()) {
      formControl.patchValue(this.getInclineMinVal());
    }
    if (formControl.value > this.getInclineMaxVal()) {
      formControl.patchValue(this.getInclineMaxVal());
    }
  }

  changeRightInputField(formControl: FormControl): void {
    this.selectionInfo.rootScopeData.isPageHasUnsavedChanges = true;
    if (formControl.value < this.getSpeedMinVal()) {
      formControl.patchValue(this.getSpeedMinVal());
    }
    if (formControl.value > this.getSpeedMaxVal()) {
      formControl.patchValue(this.getSpeedMaxVal());
    }
  }

  changedLayout(layout: string): void {
    this.selectionInfo.rootScopeData.isPageHasUnsavedChanges = true;
    this._changedLayout(layout);
  }

  getInclineMinVal(): number | void {
    if (!this.selectionInfo.activityId) {
      this.selectionInfo.activityId = this.selectionInfo.activityId;
    }
    if (this.selectionInfo.activityId === '28') {
      if (this.getModalAMyles()) {
        return this.performanceRunData.inclineMinVal_std;
      }
      if (this.getModelBMyles()) {
        return this.performanceRunData.inclineMinVal_high;
      }
    }
    if (this.selectionInfo.activityId === '1') {
      return this.performanceRunData.inclineMinVal;
    }
  }

  getInclineMaxVal(): number | void {
    if (this.selectionInfo.activityId === '28') {
      if (this.getModalAMyles()) {
        return this.performanceRunData.inclineMaxVal_std;
      }
      if (this.getModelBMyles()) {
        return this.performanceRunData.inclineMaxVal_high;
      }
    }
    if (this.selectionInfo.activityId === '1') {
      return this.performanceRunData.inclineMaxVal;
    }
  }

  getSpeedMinVal(): number | void {
    if (this.selectionInfo.activityId === '28') {
      if (this.getModalAMyles()) {
        return this.performanceRunData.speedMinVal_std;
      }
      if (this.getModelBMyles()) {
        return this.performanceRunData.speedMinVal_high;
      }
    }
    if (this.selectionInfo.activityId === '1') {
      return this.performanceRunData.speedMinVal;
    }
  }

  getSpeedMaxVal(): number | void {
    if (this.selectionInfo.activityId === '28') {
      if (this.getModalAMyles()) {
        return this.performanceRunData.speedMaxVal_std;
      }
      if (this.getModelBMyles()) {
        return this.performanceRunData.speedMaxVal_high;
      }
    }
    if (this.selectionInfo.activityId === '1') {
      return this.performanceRunData.speedMaxVal;
    }
  }

  getLeftList() {
    if (this.getModelBMyles() && this.selectionInfo.activityId === '28') {
      if (this.performanceRunData.showSixRowLayout) {
        return (this.performanceRunForm.controls.visibleSixRowLayoutValues_highIncludeKeys as FormArray).controls;
      }
      return (this.performanceRunForm.controls.visibleTenRowLayoutValues_highIncludeKeys as FormArray).controls;
    }
    if (this.performanceRunData.showSixRowLayout) {
      return (this.performanceRunForm.controls.visibleSixRowLayoutValuesIncludeKeys as FormArray).controls;
    }
    return (this.performanceRunForm.controls.visibleTenRowLayoutValuesIncludeKeys as FormArray).controls;
  }

  getLeftListForImage() {
    let list = [...this.getLeftList()];
    const reverseList = list.reverse();
    return reverseList;
  }

  getRightList() {
    if (this.getModelBMyles() && this.selectionInfo.activityId === '28') {
      if (this.performanceRunData.showSixRowLayout) {
        return (this.performanceRunForm.controls.visibleSixRowLayoutValues_highSpeedKeys as FormArray).controls;
      }
      return (this.performanceRunForm.controls.visibleTenRowLayoutValues_highSpeedKeys as FormArray).controls;
    }
    if (this.performanceRunData.showSixRowLayout) {
      return (this.performanceRunForm.controls.visibleSixRowLayoutValuesSpeedKeys as FormArray).controls;
    }
    return (this.performanceRunForm.controls.visibleTenRowLayoutValuesSpeedKeys as FormArray).controls;
  }

  getRightListForImage() {
    let list = [...this.getRightList()];
    const reverseList = list.reverse();
    return reverseList;
  }

  getModalAMyles(): boolean {
    return this.selectionInfo.facility.summary?.mylesTread == 0 || this.selectionInfo.facility.summary?.mylesTread == 2;
  }

  getModelBMyles(): boolean {
    return (
      (this.selectionInfo.facility.summary.mylesTread == 1 || this.selectionInfo.facility.summary.mylesTread == 2) &&
      this.selectionInfo.activityId === '28'
    );
  }

  private _changedLayout(layout: string): void {
    if (layout === '6') {
      this.performanceRunData.showSixRowLayout = true;
      this.performanceRunData.showTenRowLayout = false;
    } else {
      this.performanceRunData.showSixRowLayout = false;
      this.performanceRunData.showTenRowLayout = true;
    }
    this.performanceRunForm = this._initializationPerformanceForm();
    this._patchPerformanceForm();
    this._patchLayoutValue(layout);
    this._setCorrespondingConsoleImage();
  }

  private _setCorrespondingConsoleImage(): void {
    const item = this._consoleImageMap.get(this.selectionInfo.consoleType);
    if (item) {
      this.selectedLayoutImage = `${this.haloWebCloudBasePath}${
        item[this.performanceRunForm.controls.layout.value === '6' ? 'sixLayout' : 'tenLayout']
      }`;
    }
  }

  private _getPerformanceRunData(): void {
    this._loader.startProcess();
    this._performanceRunService
      .getPerformanceRunData(this._getRequestPayload())
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => this._loader.endProcess())
      )
      .subscribe((performanceRunData: PerformanceRun) => {
        if (performanceRunData) {
          this.performanceRunData = performanceRunData;
          this.performanceRunForm = this._initializationPerformanceForm();
          this._patchPerformanceForm();
          this._setCorrespondingConsoleImage();
          this._loader.endProcess();
        }
      });
  }

  private _getSavePerformanceRunRequestPayload() {
    let body: any = {
      activityId: this.selectionInfo.activityId,
      assetIds: this.assetIds,
      preferredUnit: this.performanceRunData.preferredUnit,
      performanceRun: {
        consoleType: this.selectionInfo.consoleType,
        facilityId: this.selectionInfo.facility.facilityId,
        layout: this.performanceRunForm.controls.layout.value,
        userId: this.selectionInfo.rootScopeData.currentUser.userId,
        performanceRunKeys: this._getPerformanceRunKeys('1'),
      },
    };

    if (this.selectionInfo.activityId === '28') {
      body = {
        ...body,
        performanceRun: {
          ...body.performanceRun,
          performanceRunHighKeys: this._getPerformanceRunKeys('28'),
        },
      };
    }

    return body;
  }

  private _getPerformanceRunKeys(activityId: string): any {
    let performanceRunKeys: any[] = [];

    if (this.getModelBMyles() && activityId === '28') {
      if (this.performanceRunForm.value.layout === '6') {
        this._fillingPerformanceRunKeys(
          performanceRunKeys,
          VisibleRowLayout.visibleSixRowLayoutValues_highIncludeKeys,
          PerformanceRunKeysType.includeKey
        );
        this._fillingPerformanceRunKeys(
          performanceRunKeys,
          VisibleRowLayout.visibleSixRowLayoutValues_highSpeedKeys,
          PerformanceRunKeysType.speedKeys
        );
      } else {
        this._fillingPerformanceRunKeys(
          performanceRunKeys,
          VisibleRowLayout.visibleTenRowLayoutValues_highIncludeKeys,
          PerformanceRunKeysType.includeKey
        );
        this._fillingPerformanceRunKeys(
          performanceRunKeys,
          VisibleRowLayout.visibleTenRowLayoutValues_highSpeedKeys,
          PerformanceRunKeysType.speedKeys
        );
      }
    } else {
      if (this.performanceRunForm.value.layout === '6') {
        this._fillingPerformanceRunKeys(
          performanceRunKeys,
          VisibleRowLayout.visibleSixRowLayoutValuesIncludeKeys,
          PerformanceRunKeysType.includeKey
        );
        this._fillingPerformanceRunKeys(
          performanceRunKeys,
          VisibleRowLayout.visibleSixRowLayoutValuesSpeedKeys,
          PerformanceRunKeysType.speedKeys
        );
      } else {
        this._fillingPerformanceRunKeys(
          performanceRunKeys,
          VisibleRowLayout.visibleTenRowLayoutValuesIncludeKeys,
          PerformanceRunKeysType.includeKey
        );
        this._fillingPerformanceRunKeys(
          performanceRunKeys,
          VisibleRowLayout.visibleTenRowLayoutValuesSpeedKeys,
          PerformanceRunKeysType.speedKeys
        );
      }
    }
    return performanceRunKeys;
  }

  private _fillingPerformanceRunKeys(performanceRunKeys: any[], list: string, type: string): any {
    this.performanceRunForm.value[list].forEach((item: any, index: number) => {
      if (type === PerformanceRunKeysType.includeKey) {
        let itemKey: any = { orderId: index + 1, incline: item.toString() };
        if (this.performanceRunData.preferredUnit === 'M') {
          itemKey = {
            ...itemKey,
            speedMt: '',
          };
        } else {
          itemKey = {
            ...itemKey,
            speedEn: '',
          };
        }
        performanceRunKeys.push(itemKey);
      } else {
        this.performanceRunData.preferredUnit === 'M'
          ? (performanceRunKeys[index].speedMt = item.toString())
          : (performanceRunKeys[index].speedEn = item.toString());
      }
    });
  }

  private _getRequestPayload() {
    return {
      facilityId: this.selectionInfo.facility.facilityId,
      consoleType: this.selectionInfo.consoleType,
      activityId: this.selectionInfo.activityId,
    };
  }

  private _checkFullPermission(): void {
    this.hasFullPermission = this.selectionInfo.facility.summary.userRights.customize === 'F';
  }

  private _initializationPerformanceForm(): FormGroup {
    return new FormGroup({
      layout: new FormControl({ value: null, disabled: !this.hasFullPermission }, Validators.required),
      visibleSixRowLayoutValuesIncludeKeys: new FormArray([]),
      visibleSixRowLayoutValuesSpeedKeys: new FormArray([]),

      visibleTenRowLayoutValuesIncludeKeys: new FormArray([]),
      visibleTenRowLayoutValuesSpeedKeys: new FormArray([]),

      visibleSixRowLayoutValues_highIncludeKeys: new FormArray([]),
      visibleSixRowLayoutValues_highSpeedKeys: new FormArray([]),

      visibleTenRowLayoutValues_highIncludeKeys: new FormArray([]),
      visibleTenRowLayoutValues_highSpeedKeys: new FormArray([]),
    });
  }

  private _patchPerformanceForm(): void {
    this._patchLayoutValue(this.performanceRunData.layoutType);
    this._patchVisibleSixRowLayoutIncludeKeysValues();
    this._patchVisibleSixRowLayoutSpeedKeysValues();

    this._patchVisibleTenRowLayoutIncludeKeysValues();
    this._patchVisibleTenRowLayoutSpeedKeysValues();

    this._patchVisibleSixRowLayoutIncludeKeyValuesHigh();
    this._patchVisibleSixRowLayoutSpeedKeysValuesHigh();

    this._patchVisibleTenRowLayoutIncludeKeyValuesHigh();
    this._patchVisibleTenRowLayoutSpeedKeysValuesHigh();
  }

  private _patchLayoutValue(layout: string | undefined): void {
    this.performanceRunForm.patchValue({
      layout: layout,
    });
  }

  private _patchVisibleSixRowLayoutIncludeKeysValues(): void {
    if (this.performanceRunData.visibleSixRowLayoutValues) {
      this.performanceRunData.visibleSixRowLayoutValues.forEach((row) => {
        this._addPerformanceRunItem(VisibleRowLayout.visibleSixRowLayoutValuesIncludeKeys, +row.incline);
      });
    }
  }

  private _patchVisibleSixRowLayoutSpeedKeysValues(): void {
    if (this.performanceRunData.visibleSixRowLayoutValues) {
      this.performanceRunData.visibleSixRowLayoutValues.forEach((row) => {
        this._addPerformanceRunItem(
          VisibleRowLayout.visibleSixRowLayoutValuesSpeedKeys,
          this.performanceRunData.preferredUnit === 'I' ? +row.speedEn : +row.speedMt
        );
      });
    }
  }

  private _patchVisibleTenRowLayoutIncludeKeysValues(): void {
    if (this.performanceRunData.visibleTenRowLayoutValues) {
      this.performanceRunData.visibleTenRowLayoutValues.forEach((row) => {
        this._addPerformanceRunItem(VisibleRowLayout.visibleTenRowLayoutValuesIncludeKeys, +row.incline);
      });
    }
  }

  private _patchVisibleTenRowLayoutSpeedKeysValues(): void {
    if (this.performanceRunData.visibleTenRowLayoutValues) {
      this.performanceRunData.visibleTenRowLayoutValues.forEach((row) => {
        this._addPerformanceRunItem(
          VisibleRowLayout.visibleTenRowLayoutValuesSpeedKeys,
          this.performanceRunData.preferredUnit === 'I' ? +row.speedEn : +row.speedMt
        );
      });
    }
  }

  private _patchVisibleSixRowLayoutIncludeKeyValuesHigh(): void {
    if (this.performanceRunData.visibleSixRowLayoutValues_high) {
      this.performanceRunData.visibleSixRowLayoutValues_high.forEach((row) => {
        this._addPerformanceRunItem(VisibleRowLayout.visibleSixRowLayoutValues_highIncludeKeys, +row.incline);
      });
    }
  }

  private _patchVisibleSixRowLayoutSpeedKeysValuesHigh(): void {
    if (this.performanceRunData.visibleSixRowLayoutValues_high) {
      this.performanceRunData.visibleSixRowLayoutValues_high.forEach((row) => {
        this._addPerformanceRunItem(
          VisibleRowLayout.visibleSixRowLayoutValues_highSpeedKeys,
          this.performanceRunData.preferredUnit === 'I' ? +row.speedEn : +row.speedMt
        );
      });
    }
  }

  private _patchVisibleTenRowLayoutIncludeKeyValuesHigh(): void {
    if (this.performanceRunData.visibleTenRowLayoutValues_high) {
      this.performanceRunData.visibleTenRowLayoutValues_high.forEach((row) => {
        this._addPerformanceRunItem(VisibleRowLayout.visibleTenRowLayoutValues_highIncludeKeys, +row.incline);
      });
    }
  }

  private _patchVisibleTenRowLayoutSpeedKeysValuesHigh(): void {
    if (this.performanceRunData.visibleTenRowLayoutValues_high) {
      this.performanceRunData.visibleTenRowLayoutValues_high.forEach((row) => {
        this._addPerformanceRunItem(
          VisibleRowLayout.visibleTenRowLayoutValues_highSpeedKeys,
          this.performanceRunData.preferredUnit === 'I' ? +row.speedEn : +row.speedMt
        );
      });
    }
  }

  private _addPerformanceRunItem(formArrayName: string, value: number): void {
    (this.performanceRunForm.controls[formArrayName] as FormArray).push(
      new FormControl({ value, disabled: !this.hasFullPermission }, Validators.required)
    );
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private onSelectionChange(selectionInfo: SelectionInfo) {
    setTimeout(() => {
      this.selectionInfo = selectionInfo;
      if (this.selectionInfo.facility.summary.facilityRights.performanceRun === 'true') {
        this._checkFullPermission();
        this.performanceRunForm = this._initializationPerformanceForm();
        this._getPerformanceRunData();
      } else {
        this._commonUtil.redirectToAvailablePage();
      }
    });
  }
}
